
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { portfolio } from '../../mocks/ru/common';
import { portfolio as portfolioFr } from '../../mocks/fr/common';
import { portfolio as portfolioEn } from '../../mocks/en/common';
import { portfolio as portfolioRo } from '../../mocks/ro/common';

const PortfolioPage = () => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [expandedCategory, setExpandedCategory] = useState<string | null>('Interior Finishing');

  const getPortfolio = () => {
    switch (currentLanguage) {
      case 'fr': return portfolioFr;
      case 'en': return portfolioEn;
      case 'ro': return portfolioRo;
      default: return portfolio;
    }
  };

  const getTexts = () => {
    switch (currentLanguage) {
      case 'fr':
        return {
          title: 'Notre Portefeuille',
          subtitle: 'Découvrez nos réalisations dans les cantons de Vaud et Genève',
          viewDetails: 'Voir les détails',
          categories: {
            'Interior Finishing': 'Finitions Intérieures',
            'Painting & Coatings': 'Peinture et Revêtements',
            'Floor Coverings': 'Revêtements de Sol',
            'Facades & Insulation': 'Façades et Isolation',
            'Special Projects': 'Projets Spéciaux'
          },
          categoryDescriptions: {
            'Interior Finishing': 'Travaux de plâtrerie, menuiserie, finitions intérieures complètes',
            'Painting & Coatings': 'Peinture murale, plafonds, finitions décoratives',
            'Floor Coverings': 'Parquet, carrelage, moquettes et autres revêtements',
            'Facades & Insulation': 'Isolation thermique, rénovation de façades, crépissage',
            'Special Projects': 'Rénovations complètes, transformations, projets sur mesure'
          }
        };
      case 'en':
        return {
          title: 'Our Portfolio',
          subtitle: 'Discover our achievements in Vaud and Geneva cantons',
          viewDetails: 'View Details',
          categories: {
            'Interior Finishing': 'Interior Finishing',
            'Painting & Coatings': 'Painting & Coatings',
            'Floor Coverings': 'Floor Coverings',
            'Facades & Insulation': 'Facades & Insulation',
            'Special Projects': 'Special Projects'
          },
          categoryDescriptions: {
            'Interior Finishing': 'Plastering works, woodwork, complete interior finishing',
            'Painting & Coatings': 'Wall painting, ceilings, decorative finishes',
            'Floor Coverings': 'Parquet, tiles, carpets and other coverings',
            'Facades & Insulation': 'Thermal insulation, facade renovation, rendering',
            'Special Projects': 'Complete renovations, transformations, custom projects'
          }
        };
      case 'ro':
        return {
          title: 'Portofoliul Nostru',
          subtitle: 'Descoperiți realizările noastre în cantonele Vaud și Geneva',
          viewDetails: 'Vezi detalii',
          categories: {
            'Interior Finishing': 'Finisaje Interioare',
            'Painting & Coatings': 'Vopsitorie și Acoperiri',
            'Floor Coverings': 'Pardoseli',
            'Facades & Insulation': 'Fațade și Izolație',
            'Special Projects': 'Proiecte Speciale'
          },
          categoryDescriptions: {
            'Interior Finishing': 'Lucrări de tencuială, tâmplărie, finisaje interioare complete',
            'Painting & Coatings': 'Vopsirea pereților, tavanelor, finisaje decorative',
            'Floor Coverings': 'Parchet, gresie, mochete și alte acoperiri',
            'Facades & Insulation': 'Izolație termică, renovarea fațadelor, tencuială',
            'Special Projects': 'Renovări complete, transformări, proiecte personalizate'
          }
        };
      default:
        return {
          title: 'Наше Портфолио',
          subtitle: 'Ознакомьтесь с нашими достижениями в кантонах Во и Женева',
          viewDetails: 'Подробнее',
          categories: {
            'Interior Finishing': 'Внутренняя Отделка',
            'Painting & Coatings': 'Покраска и Покрытия',
            'Floor Coverings': 'Напольные Покрытия',
            'Facades & Insulation': 'Фасады и Утепление',
            'Special Projects': 'Специальные Проекты'
          },
          categoryDescriptions: {
            'Interior Finishing': 'Штукатурные работы, столярка, полная внутренняя отделка',
            'Painting & Coatings': 'Покраска стен, потолков, декоративные покрытия',
            'Floor Coverings': 'Паркет, плитка, ковры и другие покрытия',
            'Facades & Insulation': 'Теплоизоляция, реновация фасадов, штукатурка',
            'Special Projects': 'Полные реновации, трансформации, индивидуальные проекты'
          }
        };
    }
  };

  const texts = getTexts();
  const currentPortfolio = getPortfolio();

  const categories = ['Interior Finishing', 'Painting & Coatings', 'Floor Coverings', 'Facades & Insulation', 'Special Projects'];

  const getCategoryProjects = (category: string) => {
    return currentPortfolio.filter(project => project.category === category);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {texts.title}
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              {texts.subtitle}
            </p>
          </div>
        </section>

        {/* Categories with Galleries */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {categories.map((category, index) => {
              const categoryProjects = getCategoryProjects(category);
              const isExpanded = expandedCategory === category;
              
              return (
                <div key={category} className={`mb-16 ${index !== 0 ? 'pt-16 border-t border-gray-200' : ''}`}>
                  {/* Category Header */}
                  <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                      {texts.categories[category as keyof typeof texts.categories]}
                    </h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                      {texts.categoryDescriptions[category as keyof typeof texts.categoryDescriptions]}
                    </p>
                  </div>

                  {/* Projects Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {categoryProjects.map((project) => (
                      <div key={project.id} className="group cursor-pointer">
                        <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
                          <div className="relative overflow-hidden">
                            <img
                              src={project.image}
                              alt={project.title}
                              className="w-full h-64 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute top-4 left-4">
                              <span className="bg-blue-600 text-white text-xs px-3 py-1 rounded-full font-medium">
                                {project.location}
                              </span>
                            </div>
                          </div>
                          <div className="p-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                              {project.title}
                            </h3>
                            <p className="text-gray-600 leading-relaxed mb-4">
                              {project.description}
                            </p>
                            <div className="flex items-center text-blue-600 font-medium">
                              <span className="text-sm whitespace-nowrap">{texts.viewDetails}</span>
                              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {categoryProjects.length === 0 && (
                    <div className="text-center py-12">
                      <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                        <i className="ri-image-line text-3xl text-gray-400"></i>
                      </div>
                      <p className="text-gray-500 text-lg">No projects found in this category</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PortfolioPage;
